import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import { cn } from "~/lib/utils";

interface MapMarkerProps {
  map: mapboxgl.Map | null;
  coordinates: [number, number] | null;
  children?: React.ReactNode;
  size?: "sm" | "md";
  className?: string;
  onClick?: () => void;
  title?: string;
  /** Optional label to display below the marker */
  label?: string;
}

/**
 * Renders a styled marker on the map with optional React content (e.g., an icon).
 * Uses portals similar to MapPopup.
 */
export function MapMarker({
  map,
  coordinates,
  children,
  size = "md",
  className,
  onClick,
  title,
  label,
}: MapMarkerProps) {
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const contentRef = useRef<HTMLDivElement>(document.createElement("div"));

  useEffect(() => {
    if (!map) return;

    // Remove existing marker
    markerRef.current?.remove();
    markerRef.current = null;

    if (coordinates) {
      markerRef.current = new mapboxgl.Marker(contentRef.current).setLngLat(coordinates).addTo(map);
    }

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
    };
  }, [map, coordinates]);

  return createPortal(
    <div
      className={cn("flex flex-col items-center", {
        "cursor-pointer": !!onClick,
      })}
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
      title={title}
    >
      <div
        className={cn(
          "bg-primary rounded-full border-2 border-primary-foreground shadow-lg grid place-items-center",
          { "size-8": size === "md", "size-6": size === "sm" },
          className,
        )}
      >
        {children}
      </div>
      {label && (
        <span className="mt-0.5 max-w-36 text-center font-sans text-sm leading-4 font-medium text-foreground [text-shadow:_-1px_-1px_0_#fff,_1px_-1px_0_#fff,_-1px_1px_0_#fff,_1px_1px_0_#fff,_0_-1px_0_#fff,_0_1px_0_#fff,_-1px_0_0_#fff,_1px_0_0_#fff]">
          {label}
        </span>
      )}
    </div>,
    contentRef.current,
  );
}
